import {
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { safeApiCall } from "api/common.service";
import { getRptSalesPerson } from "api/report.service";
import { navigateTo } from "common/navigation";
import { formatDate2, formatNZD } from "common/utils";
import PreviewHeader from "components/header/PreviewHeader";
import React, { useEffect, useState } from "react";
import { RptSalesPersons } from "types/model.types";

const AdminSalesPersonReportPage: React.FC = () => {
  const [data, setData] = useState<RptSalesPersons>([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const apiFetchRef = React.useRef<boolean>(false);
  const [printing, setPrinting] = useState(false);

  const fetchData = async () => {
    if (apiFetchRef.current === true) return;
    apiFetchRef.current = true;
    setLoading(true);

    const result = await safeApiCall(getRptSalesPerson(fromDate, toDate));
    setData(result?.data ?? []);
    apiFetchRef.current = false;
    setLastUpdated(new Date());
    setLoading(false);
  };

  useEffect(() => {
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1); // Jan 1st

    setFromDate(startOfYear);
    setToDate(today);

    fetchData(); // initial load with null dates
  }, []);

  const grandTotal = data.reduce((sum, row) => sum + (row.totalSales ?? 0), 0);
  return (
    <Stack>
      <Group justify="space-between" align="end" gap="sm">
        <Title order={2}>Sales Person Report</Title>
        <Button
          onClick={() => {
            navigateTo(
              `/print/sales-person?f=${formatDate2(fromDate ?? new Date())}&t=${formatDate2(toDate ?? new Date())}`,
            );
          }}
          variant="gradient"
        >
          Print
        </Button>
      </Group>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          <Group justify="space-between" align="end" gap="sm">
            <Group align="end" gap="sm">
              <DatePickerInput
                label="From Date"
                value={fromDate}
                dropdownType="modal"
                modalProps={{
                  zIndex: 3000,
                  size: "sm",
                  withinPortal: true,
                  trapFocus: false,
                  centered: true,
                  keepMounted: true,
                }}
                onChange={(value: string | null) =>
                  setFromDate(value ? new Date(value) : null)
                }
                clearable
                w={250}
              />
              <DatePickerInput
                label="To Date"
                value={toDate}
                dropdownType="modal"
                modalProps={{
                  zIndex: 3000,
                  size: "sm",
                  withinPortal: true,
                  trapFocus: false,
                  centered: true,
                  keepMounted: true,
                }}
                onChange={(value: string | null) =>
                  setToDate(value ? new Date(value) : null)
                }
                w={250}
                clearable
              />
              <Button onClick={fetchData}>Refresh</Button>
            </Group>
          </Group>
          <div id="preview-content">
            {printing && (
              <>
                <PreviewHeader />
                <Center w={"100%"} mb="md" style={{ marginBottom: "1rem" }}>
                  <Text size="lg" style={{ fontSize: "1.3rem" }} fw={700}>
                    *** Sales Persons Report from{" "}
                    {formatDate2(fromDate ?? new Date())} to{" "}
                    {formatDate2(toDate ?? new Date())} ***
                  </Text>
                </Center>
              </>
            )}
            <Table
              striped
              highlightOnHover
              withTableBorder
              withColumnBorders
              className="print-table"
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Sales Person</Table.Th>
                  <Table.Th className="alignr">Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.map((row, index) => (
                  <Table.Tr key={`${index}-${row.salesPerson}`}>
                    <Table.Td>{row.salesPerson}</Table.Td>
                    <Table.Td className="alignr">
                      {formatNZD(row.totalSales)}
                    </Table.Td>
                  </Table.Tr>
                ))}
                <Table.Tr>
                  <Table.Th></Table.Th>
                  <Table.Th className="alignr money">
                    {formatNZD(grandTotal)}
                  </Table.Th>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </div>
        </>
      )}
    </Stack>
  );
};

export default AdminSalesPersonReportPage;
